import React, { useMemo } from 'react';
import { PostQueueStat } from '../../containers/PostQueueStat';
import { PostQueueStatLabelsEnum } from '../../containers/PostQueueStat/PostQueueStat.view';
import { getRelevantStats, statisticsCondition } from '../helpers/getStatisticsCondition';
import { Tooltip } from '../../actions/Tooltip/';
import { CardOrientationEnum } from '../BasePostCard/BasePostCard.view';
import classes from './PostCardStatisticsTable.module.scss';
import baseCardClasses from '../BasePostCard/BasePostCard.module.scss';
import classNames from 'classnames';
import { Button } from '../../actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { SocialNetworksEnum } from '../../../constants/social-networks';
import { PostScheduleViewContext } from '../../../modules/Publish/PostSchedule/PostSchedule.controller';
import { PostItemFragment } from '../../../models/graphql';
import { idGenerator } from '../../../helpers/idGenerator';
import scssVars from '../../../assets/scss/exportedVars.module.scss';

export type PostCardStatisticsTableViewProps = {
  post: PostItemFragment;
  context: PostScheduleViewContext;
  orientation: CardOrientationEnum;
  showBarGraph?: boolean;
  openDetailsModal?: VoidFunction;
  isAggratedTable?: boolean;
  isDisabled?: boolean;
};

const getStatValue = (
  post: PostItemFragment,
  stat: PostQueueStatLabelsEnum,
  context?: PostScheduleViewContext,
  isAggratedTable?: boolean
): number => {
  const statsMap: Record<string, number | undefined | null> =
    context === PostScheduleViewContext.LIBRARY || isAggratedTable
      ? {
          [PostQueueStatLabelsEnum.clicks]: post.iterationAggregations?.sumClicks || 0,
          [PostQueueStatLabelsEnum.likes]: post.iterationAggregations?.sumLikes || 0,
          [PostQueueStatLabelsEnum.share]: post.iterationAggregations?.sumShares || 0,
          [PostQueueStatLabelsEnum.comments]: post.iterationAggregations?.sumComments || 0,
          [PostQueueStatLabelsEnum.impressions]: post.iterationAggregations?.sumImpressions || 0,
          [PostQueueStatLabelsEnum.videoViews]: post.iterationAggregations?.sumVideoViews || 0,
          [PostQueueStatLabelsEnum.boost]: Math.round(post.iterationAggregations?.boost || 0)
        }
      : {
          [PostQueueStatLabelsEnum.clicks]: post.publishedStats?.clicks || 0,
          [PostQueueStatLabelsEnum.likes]: post.publishedStats?.likes || 0,
          [PostQueueStatLabelsEnum.share]: post.publishedStats?.shares || 0,
          [PostQueueStatLabelsEnum.comments]: post.publishedStats?.comments || 0,
          [PostQueueStatLabelsEnum.impressions]: post.publishedStats?.impressions || 0,
          [PostQueueStatLabelsEnum.videoViews]: post.publishedStats?.videoViews || 0,
          [PostQueueStatLabelsEnum.boost]: Math.round(post.iterationAggregations?.boost || 0)
        };

  return statsMap[stat] ?? 0;
};

const getMaxStatValue = (post: PostItemFragment, stat: PostQueueStatLabelsEnum): number => {
  const statsMap: Record<string, number | undefined> = {
    [PostQueueStatLabelsEnum.likes]: post.iterationAggregations?.maxLikes || -1,
    [PostQueueStatLabelsEnum.comments]: post.iterationAggregations?.maxComments || -1,
    [PostQueueStatLabelsEnum.share]: post.iterationAggregations?.maxShares || -1,
    [PostQueueStatLabelsEnum.impressions]: post.iterationAggregations?.maxImpressions || -1,
    [PostQueueStatLabelsEnum.videoViews]: post.iterationAggregations?.maxVideoViews || -1,
    [PostQueueStatLabelsEnum.clicks]: post.iterationAggregations?.maxClicks || -1,
    [PostQueueStatLabelsEnum.boost]: post.iterationAggregations?.boost || -1
  };

  return statsMap[stat] ?? 0;
};

export const getTooltipMessage = (
  stat: PostQueueStatLabelsEnum,
  network: SocialNetworksEnum | null,
  isDisabled: boolean
): string => {
  if (network === SocialNetworksEnum.instagram && isDisabled) {
    return 'These stats are only available for Instagram Profiles connected via a Facebook Page';
  }
  const tooltipMessages: Record<string, string> = {
    [PostQueueStatLabelsEnum.likes]: 'The total number of users who like this post',
    [PostQueueStatLabelsEnum.comments]: 'The total number of comments/replies on this post',
    [PostQueueStatLabelsEnum.share]: 'The number of times this post was shared',
    [PostQueueStatLabelsEnum.impressions]: "The total number of times this post was displayed on users' screens",
    [PostQueueStatLabelsEnum.videoViews]: 'The total number of times this video was viewed',
    [PostQueueStatLabelsEnum.clicks]: 'The number of times users clicked on a link within this post',
    [PostQueueStatLabelsEnum.boost]:
      'The SmarterQueue Boost is the total increase in engagement on an Evergreen post compared to if you had only shared it once'
  };

  return tooltipMessages[stat] ?? '';
};

export const PostCardStatisticsTableView = (props: PostCardStatisticsTableViewProps): JSX.Element => {
  const { post, orientation, showBarGraph, openDetailsModal, context, isAggratedTable } = props;
  const hideLabel = orientation === CardOrientationEnum.PORTRAIT;
  const tableId = useMemo(() => idGenerator(), []);
  const statConditions = statisticsCondition(post, context, isAggratedTable);
  const relevantStats = statConditions.network
    ? getRelevantStats(statConditions.network, post, context, isAggratedTable)
    : [];

  const statTableContainerClasses = classNames('grid-x', baseCardClasses.StatsTable, {
    [baseCardClasses.StatsTableLandscape]: orientation === CardOrientationEnum.LANDSCAPE,
    [baseCardClasses.StatsTablePortrait]: orientation === CardOrientationEnum.PORTRAIT
  });

  const statTableWrapperClasses = classNames('cell', classes.StatTableWrapperClasses, {
    [classes.StatTableWrapperClassesHidden]: orientation === CardOrientationEnum.PORTRAIT && relevantStats.length === 0,
    [classes.StatTableWrapperClassesLandscape]: orientation === CardOrientationEnum.LANDSCAPE,
    [classes.StatTableWrapperClassesLandscapeWithGraph]: orientation === CardOrientationEnum.LANDSCAPE && showBarGraph,

    auto: orientation !== CardOrientationEnum.PORTRAIT,
    'cell-12 margin-top-small-8 margin-bottom-small-8': orientation === CardOrientationEnum.PORTRAIT
  });

  const statsButtonContainer = classNames('cell', baseCardClasses.DetailStatsButtonContainer, {
    shrink: orientation !== CardOrientationEnum.PORTRAIT,
    'cell-12': orientation === CardOrientationEnum.PORTRAIT
  });

  const statItemClasses = classNames(baseCardClasses.StatsItem, {
    [baseCardClasses.StatsItemLandscape]: orientation === CardOrientationEnum.LANDSCAPE,
    [baseCardClasses.StatsItemPortrait]: orientation === CardOrientationEnum.PORTRAIT
  });

  const statButton = (
    <div className={statsButtonContainer}>
      <Button
        type={'button'}
        variant={'hollow'}
        size={'small'}
        color={ButtonColorsEnum.primary}
        action={openDetailsModal}
        expanded={orientation !== CardOrientationEnum.LANDSCAPE || undefined}>
        {`View stats For ${post.iterationAggregations?.countTimesPosted ?? 'N/A'} Cycle${
          post.iterationAggregations?.countTimesPosted && post.iterationAggregations.countTimesPosted > 1 ? 's' : ''
        }`}
      </Button>
    </div>
  );

  return statConditions.show && (!statConditions.message || context === PostScheduleViewContext.LIBRARY) ? (
    <div className={statTableContainerClasses}>
      <div className={statTableWrapperClasses}>
        {relevantStats.map(({ stat, isDisabled }, index) => {
          return (
            <div key={stat} className={statItemClasses} id={`post-${tableId}-${index}-tooltip`}>
              <PostQueueStat
                label={stat}
                value={getStatValue(post, stat, context, isAggratedTable)}
                hideLabel={hideLabel}
                isDisabled={isDisabled}
                {...(!!showBarGraph && !isAggratedTable
                  ? { scale: getMaxStatValue(post, stat), showBarGraph: true }
                  : { showBarGraph: false })}
              />
              <Tooltip
                className={classes.StatTooltipWrapper}
                target={`#post-${tableId}-${index}-tooltip`}
                type={'regular'}
                autoZIndex={false}
                style={{ zIndex: scssVars['z-index-overmodal'] }}
                position={'bottom'}
                pointer>
                {getTooltipMessage(stat, statConditions.network, isDisabled)}
              </Tooltip>
            </div>
          );
        })}
      </div>
      {openDetailsModal && statButton}
    </div>
  ) : statConditions.message ? (
    <div className={`grid-x align-justify ${classes.StatisticsInformationalWrapper}`}>
      <div className={`cell auto ${classes.StatisticsInformationalText}`}>{statConditions.message}</div>
    </div>
  ) : (
    <></>
  );
};
